import { REQUEST_STATUS, PAYMENT_INTENT_STATUS } from "../../../../models/EnrollmentHistoryDTO";
import { ahoraCdmx } from "../../../../utils/horaMexico";

/** Cómo se escribe un horario en las listas y fichas de estos dos módulos: "Domingos 4:00 pm – 7:30 pm". */
export const HORARIO_LISTA = { plural: true, separador: " " };

/*
	Una solicitud aprobada pero con el pago todavía en Caja no puede verse igual de verde que una
	ya inscrita: se distingue por la etiqueta que manda el servidor (Esteban, 2026-09-23).
*/
export const estadoVariant = (statusId: number, etiqueta?: string | null): string =>
	statusId === REQUEST_STATUS.APROBADA && (etiqueta || "").toLowerCase().includes("pendiente pago")
		? "oro"
		: requestStatusVariant(statusId);

/** Bootstrap badge variants per request status. */
export const requestStatusVariant = (statusId: number): string => {
	switch (statusId) {
		case REQUEST_STATUS.BORRADOR: return "secondary";
		//"En revisión" es lo que más se ve en la bandeja: en blanco sobre naranja daba 3,48 y no se leía
		//bien. La etiqueta de tinta de WAPP da contraste alto en claro y en oscuro (2026-09-23).
		case REQUEST_STATUS.ENVIADA: return "info";
		case REQUEST_STATUS.DEVUELTA: return "oro";
		case REQUEST_STATUS.APROBADA: return "success";
		case REQUEST_STATUS.CANCELADA: return "dark";
		default: return "light";
	}
};

export const intentStatusVariant = (statusId: number): string => {
	switch (statusId) {
		case PAYMENT_INTENT_STATUS.PENDIENTE: return "oro";
		case PAYMENT_INTENT_STATUS.APLICADO: return "success";
		case PAYMENT_INTENT_STATUS.PROCESADO: return "success";
		case PAYMENT_INTENT_STATUS.RECHAZADO: return "dark";
		default: return "light";
	}
};

export const formatMoney = (value: number | string | null | undefined): string => {
	if (value === null || value === undefined || value === "") {
		return "-";
	}
	const n = Number(value);
	if (isNaN(n)) {
		return "-";
	}
	return "$" + n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatDate = (value: string | null | undefined): string => {
	if (!value) {
		return "-";
	}
	const [y, m, d] = value.substring(0, 10).split("-");
	return d && m && y ? `${d}/${m}/${y}` : value;
};

export const todayIso = (): string => {
	const now = ahoraCdmx();
	const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** Opens a downloaded blob in a new tab (comprobantes, identificaciones). */
export const openBlob = (blob: Blob) => {
	const url = window.URL.createObjectURL(blob);
	window.open(url, "_blank");
	setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_FILES = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.heic,.heif";

/** Builds the `pages` array PaginationComponent expects from a simple page count. */
/** corto: en el teléfono "« Anterior" y "Siguiente »" no cabían y se partían en dos renglones. */
export const buildPages = (currentPage: number, lastPage: number, go: (page: number) => void, corto: boolean = false) => {
	const pages: any[] = [];
	pages.push({ label: corto ? "&laquo;" : "&laquo; Anterior", url: currentPage > 1 ? "prev" : null, active: false, onClick: () => go(currentPage - 1) });
	for (let p = 1; p <= Math.max(lastPage, 1); p++) {
		if (lastPage > 9 && Math.abs(p - currentPage) > 3 && p !== 1 && p !== lastPage) {
			if (pages[pages.length - 1].label !== "...") {
				pages.push({ label: "...", url: null, active: false, onClick: () => {} });
			}
			continue;
		}
		pages.push({ label: String(p), url: "page", active: p === currentPage, onClick: () => go(p) });
	}
	pages.push({ label: corto ? "&raquo;" : "Siguiente &raquo;", url: currentPage < lastPage ? "next" : null, active: false, onClick: () => go(currentPage + 1) });
	return pages;
};

export const isMinor = (birthday: string | undefined | null): boolean => {
	if (!birthday) {
		return false;
	}
	const b = new Date(birthday.substring(0, 10) + "T00:00:00");
	if (isNaN(b.getTime())) {
		return false;
	}
	const now = ahoraCdmx();
	let age = now.getFullYear() - b.getFullYear();
	const m = now.getMonth() - b.getMonth();
	if (m < 0 || (m === 0 && now.getDate() < b.getDate())) {
		age--;
	}
	return age < 18;
};

/** Error text that survives both Laravel validation payloads and our BadRequest/NotFound shapes. */
export const errorText = (error: any): string => {
	const data = error?.response?.data;
	if (data) {
		if (data.errors && typeof data.errors === "object") {
			const parts: string[] = [];
			Object.keys(data.errors).forEach((k) => { const v = data.errors[k]; parts.push(Array.isArray(v) ? v[0] : String(v)); });
			if (parts.length > 0) {
				return parts.join(" ");
			}
		}
		if (data.message) {
			return String(data.message);
		}
	}
	return error?.message ? String(error.message) : "Ocurrió un error inesperado.";
};

/*
	El número de identificación debe cuadrar con su tipo (Esteban, 2026-09-24: llegó de Ventas un DNI
	de Honduras con un dígito de menos). Las reglas son las del catálogo de WAPP (ct_identification_types):
	el DNI hondureño lleva exactamente 13 dígitos, el INE 12 o 13, la CURP su formato. Devuelve el
	problema en palabras, o null si está bien. El servidor revisa lo mismo al enviar.
*/
export const problemaIdentificacion = (codigo: string | null | undefined, tipo: any): string | null => {
	const valor = String(codigo || "").trim();
	if (!tipo || valor === "") {
		return null;
	}
	const unidad = tipo.bool_only_digits ? "dígitos" : "caracteres";
	const min = Number(tipo.min_length) || 0;
	const max = Number(tipo.max_length) || 0;
	//Todo el rigor (solo números y formato) para los documentos de Honduras; el resto, solo el largo,
	//que es lo que ya exige WAPP al guardar al alumno (Esteban, 2026-09-24: "esto en Honduras").
	const deHonduras = Number(tipo.country_id) === 2;
	if (deHonduras && tipo.bool_only_digits && /\D/.test(valor)) {
		return `${tipo.name}: solo números`;
	}
	if ((min && valor.length < min) || (max && valor.length > max)) {
		const cuantos = min === max ? `${min}` : `de ${min} a ${max}`;
		return `${tipo.name}: debe llevar ${cuantos} ${unidad} (tiene ${valor.length})`;
	}
	if (deHonduras && String(tipo.name).toUpperCase() === "DNI") {
		const malDni = problemaDni(valor);
		if (malDni) {
			return malDni;
		}
	}
	//En basicInfo el patrón llega como {value, regexp} (common.tsx); desde el servidor, como texto.
	const patron: string = typeof tipo.pattern === "string" ? tipo.pattern : (tipo.pattern?.value || "");
	if (deHonduras && patron) {
		try {
			if (!new RegExp("^(?:" + patron + ")$").test(valor)) {
				return `${tipo.name}: el formato no es correcto`;
			}
		} catch (e) {
			return null;
		}
	}
	return null;
};

/*
	DNI de Honduras (Esteban, 2026-09-24): los primeros 4 dígitos son departamento (01-18) y municipio,
	los siguientes 4 el año de nacimiento (1900 al año actual). Municipios por departamento, de la lista
	oficial (298 en total); coincide con los DNI que ya están en WAPP: solo 30 de ~18,000 caen fuera.
*/
const MUNICIPIOS_POR_DEPARTAMENTO = [8, 10, 21, 23, 12, 16, 19, 28, 6, 17, 4, 19, 28, 16, 23, 28, 9, 11];

export const problemaDni = (dni: string): string | null => {
	if (!/^\d{13}$/.test(dni)) {
		return null;
	}
	const departamento = parseInt(dni.slice(0, 2), 10);
	const municipio = parseInt(dni.slice(2, 4), 10);
	if (departamento < 1 || departamento > 18 || municipio < 1 || municipio > MUNICIPIOS_POR_DEPARTAMENTO[departamento - 1]) {
		return `DNI: los primeros 4 dígitos (${dni.slice(0, 4)}) no son un departamento y municipio de Honduras`;
	}
	const anio = parseInt(dni.slice(4, 8), 10);
	if (anio < 1900 || anio > new Date().getFullYear()) {
		return `DNI: los dígitos 5 a 8 (${dni.slice(4, 8)}) deberían ser el año de nacimiento`;
	}
	return null;
};
